import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { band } from '@/constants/portfolio'
import { useExperience } from '@/components/generals/experience-provider'

import {
    assetProgress,
    completeAsset,
    registerAsset,
    subscribeAssets
} from '@/lib/preload'

const MIN_VISIBLE_MS = 1500
const MAX_WAIT_MS = 4000

// Zine-style opening: the slogan slams in line by line while real assets load.
// It never blocks for more than four seconds and can always be skipped.
const Preloader: FC = (): ReactNode => {
    const { setIntroDone, reducedMotion } = useExperience()
    const [percent, setPercent] = useState(0)
    const [leaving, setLeaving] = useState(false)
    const [gone, setGone] = useState(false)
    const shownAt = useRef(0)
    const finished = useRef(false)
    const skipRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        shownAt.current = performance.now()

        registerAsset('fonts', 2)
        registerAsset('page', 1)
        registerAsset('crest', 2)

        document.fonts.ready.then(() => completeAsset('fonts')).catch(() => completeAsset('fonts'))

        if (document.readyState === 'complete') completeAsset('page')
        else window.addEventListener('load', () => completeAsset('page'), { once: true })

        const update = () => setPercent(Math.round(assetProgress() * 100))
        update()

        return subscribeAssets(update)
    }, [])

    useEffect(() => {
        const finish = () => {
            if (finished.current) return
            finished.current = true
            setLeaving(true)
            setIntroDone(true)
        }

        if (percent >= 100) {
            const elapsed = performance.now() - shownAt.current
            const wait = Math.max(0, MIN_VISIBLE_MS - elapsed)
            const timer = window.setTimeout(finish, wait)
            return () => window.clearTimeout(timer)
        }

        const cap = window.setTimeout(finish, Math.max(0, MAX_WAIT_MS - (performance.now() - shownAt.current)))
        return () => window.clearTimeout(cap)
    }, [percent, setIntroDone])

    useEffect(() => {
        if (!leaving) return

        const timer = window.setTimeout(() => setGone(true), reducedMotion ? 50 : 700)
        return () => window.clearTimeout(timer)
    }, [leaving, reducedMotion])

    useEffect(() => {
        document.documentElement.style.overflow = gone ? '' : 'hidden'

        return () => {
            document.documentElement.style.overflow = ''
        }
    }, [gone])

    const skip = () => {
        if (finished.current) return
        finished.current = true
        setLeaving(true)
        setIntroDone(true)
    }

    if (gone) return null

    return (
        <div
            className={cn(
                'preloader fixed inset-0 z-[110] flex flex-col justify-between bg-ink px-5 py-6 text-paper xs:px-8 tab:px-12 tab:py-10',
                leaving && 'preloader-leave'
            )}
        >
            <div className='flex items-start justify-between gap-6'>
                <p className='mono-label text-[11px] text-paper/70 xs:text-xs'>
                    {band.name} / Interactive press kit
                </p>

                <button
                    ref={skipRef}
                    type='button'
                    onClick={skip}
                    className='mono-label min-h-11 border-2 border-paper px-4 text-xs text-paper transition-colors hover:bg-paper hover:text-ink'
                >
                    Skip intro
                </button>
            </div>

            <div className='flex flex-col gap-1 tab:gap-2' aria-hidden='true'>
                {band.tagline.map((line, index) => (
                    <span key={line} className='block overflow-hidden'>
                        <span
                            className='display-type preloader-line block text-[clamp(2.6rem,11vw,10rem)]'
                            style={{ animationDelay: `${120 + index * 260}ms` }}
                        >
                            {line}
                        </span>
                    </span>
                ))}
            </div>

            <div className='flex items-end justify-between gap-6'>
                <p className='mono-label max-w-[28ch] text-[11px] text-paper/70 xs:text-xs'>
                    Pop rock / Alternative / 90s grunge. Malaysia, since late 2023.
                </p>

                <p className='display-type text-[clamp(3rem,9vw,7.5rem)] tabular-nums' role='status'>
                    <span className='sr-only'>Loading </span>
                    {percent}
                    <span className='text-[0.4em] align-top'>%</span>
                </p>
            </div>

            <div className='absolute inset-x-0 bottom-0 h-1.5 bg-paper/15' aria-hidden='true'>
                <div className='h-full origin-left bg-paper transition-transform duration-300' style={{ transform: `scaleX(${percent / 100})` }} />
            </div>
        </div>
    )
}

export default Preloader
